import React, { useRef } from 'react';
import { Box, styled } from '@mui/material';
import { getThemeColors } from '../helpers/themeStyles';

const ERROR_COLOR = '#FF6669';
const OTP_LENGTH = 6;

const OtpContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
});

const OtpDigitInput = styled('input', {
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})(({ theme, $error }) => {
  const colors = getThemeColors(theme);

  return {
    width: 44,
    height: 44,
    padding: 0,
    borderRadius: 12,
    border: `1px solid ${$error ? ERROR_COLOR : colors.inputBorder}`,
    backgroundColor: 'transparent',
    color: colors.inputText,
    fontSize: 20,
    fontWeight: 600,
    textAlign: 'center',
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: $error ? ERROR_COLOR : colors.inputFocus,
    },
  };
});

const OtpInput = ({ value = '', onChange, error = false, disabled = false }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, index) => value[index] ?? '');

  const updateValue = (nextDigits) => {
    onChange(nextDigits.join('').slice(0, OTP_LENGTH));
  };

  const focusInput = (index) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index, event) => {
    const rawValue = event.target.value.replace(/\D/g, '');
    const nextDigits = [...digits];

    if (!rawValue) {
      nextDigits[index] = '';
      updateValue(nextDigits);
      return;
    }

    const chars = rawValue.split('');
    let cursor = index;

    chars.forEach((char) => {
      if (cursor >= OTP_LENGTH) return;
      nextDigits[cursor] = char;
      cursor += 1;
    });

    updateValue(nextDigits);

    if (cursor < OTP_LENGTH) {
      focusInput(cursor);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = '';
      updateValue(nextDigits);
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

    if (!pasted) return;

    updateValue(pasted.split(''));
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  return (
    <OtpContainer>
      {digits.map((digit, index) => (
        <OtpDigitInput
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          $error={error}
          aria-label={`Chiffre ${index + 1} du code`}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </OtpContainer>
  );
};

export { OTP_LENGTH };
export default OtpInput;
